import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useAuth } from "../../../auth/AuthContext";
import { PanelLoading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import { useConfirm } from "../../../components/ConfirmDialog";
import { Select } from "../../../components/Select";
import { localizedName } from "../../../lib/localizedName";
import { formatDate } from "../../../lib/format";
import Ic from "../../../components/icons.jsx";
import type { ApiError } from "../../../api/client";
import {
  FREQUENCY_BASES,
  OUT_OF_RANGE_ACTIONS,
  QUALITY_PHASES,
  productionStepApi,
  testPlanApi,
  type OutOfRangeAction,
  type PhaseTally,
  type PhaseView,
  type PlanItem,
  type PlanItemBody,
  type ProductionStep,
  type QualityCatalogueEntry,
  type QualityFrequencyBasis,
  type QualityPhase,
  type TestPlan,
} from "../../../api/quality";
import { frequencyLabel, outOfRangeLabel, phaseHint, phaseLabel, resultTypeLabel } from "./QualityPhaseLabels";

/**
 * Konfigurator testova: katalog onoga što laboratorija ume da izmeri, i raspored tih testova po fazama.
 *
 * Isti test se radi na više mesta u lancu — vlaga na ulaznoj turi pre vage, pa opet u procesu, pa na
 * gotovom proizvodu. Zato se test definiše jednom, a plan kaže gde se koristi, sa kojim granicama,
 * koliko često i ko ga unosi. Ekran je zato dvodelan: levo plan, desno njegove stavke po fazama.
 *
 * Dve stvari ekran izričito KAŽE umesto da ih prećuti:
 * granica koja nije uneta (T1 ih nije doneo ni za jedan test), i stavka koja je opis a ne merenje
 * (vizuelni pregled zrna se potvrđuje, ne izračunava).
 *
 * PLAN SE NE MENJA NEGO SE ZAMENJUJE. Nalaz uzet po jednom planu mora i posle da se objasni pravilima
 * koja su tada važila, pa se nova propisana vrednost ne upisuje preko stare: stari plan se zatvara
 * datumom, a novi se otvara od sledećeg dana. Zato ekran nudi zamenu i povlačenje, a ne izmenu plana.
 */
export default function AdminTestPlansPage() {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  // Ekran vidi i laborant, jer mora da zna sta je propisano - bez toga ne zna sta da izmeri. Ali
  // propisivanje je posao sefa laboratorije (KK5), pa radnje postoje samo uz test_plans:manage.
  // Ruta je puštena po test_plans:view, i zato se ovde razdvaja gledanje od vodjenja.
  const smeDaVodi = hasPermission("test_plans:manage");
  const toast = useToast();
  const confirm = useConfirm();
  const lang = i18n.resolvedLanguage ?? "sr";

  const [plans, setPlans] = useState<TestPlan[] | null>(null);
  const [catalogue, setCatalogue] = useState<QualityCatalogueEntry[] | null>(null);
  const [steps, setSteps] = useState<ProductionStep[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [open, setOpen] = useState<TestPlan | null>(null);
  const [tab, setTab] = useState<"plans" | "phases" | "catalogue">("plans");
  const [error, setError] = useState<string | null>(null);
  const [addingAt, setAddingAt] = useState<QualityPhase | null>(null);
  const [editingItem, setEditingItem] = useState<PlanItem | null>(null);
  /** Nov plan, ili zamena postojećeg — isti obrazac, dva povoda. */
  const [newPlan, setNewPlan] = useState<{ replaces: TestPlan | null } | null>(null);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([testPlanApi.list(), testPlanApi.catalogue(false)]);
      setPlans(p);
      setCatalogue(c);
      if (!openId && p.length > 0) setOpenId(p[0].id);
    } catch (e) {
      setError((e as ApiError).message);
    }
  }, [openId]);

  // Koraci proizvodnje su potrebni samo obrascu stavke u fazi procesa, pa njihovo odsustvo ne sme da
  // obori ekran: plan se vodi i dok je šifarnik koraka prazan.
  useEffect(() => {
    void productionStepApi
      .list(true)
      .then(setSteps)
      .catch(() => setSteps([]));
  }, []);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOpen = useCallback(async (id: string) => {
    try {
      setOpen(await testPlanApi.get(id));
    } catch (e) {
      toast.error((e as ApiError).message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (openId) void loadOpen(openId);
  }, [openId, loadOpen]);

  /** Planovi se grupišu po predmetu: sirovine po kulturi, pa proizvodi, pa opšti. */
  const grouped = useMemo(() => {
    const rows = plans ?? [];
    return {
      cultures: rows.filter((p) => p.culture),
      products: rows.filter((p) => !p.culture && p.productId),
      general: rows.filter((p) => !p.culture && !p.productId),
    };
  }, [plans]);

  async function refreshBoth() {
    await load();
    if (openId) await loadOpen(openId);
  }

  /**
   * Povlačenje plana. Nije brisanje: potpisana pokrivenost i nalazi uzeti po njemu ostaju, a šifra ostaje
   * zauzeta da je nov plan ne preuzme i time ne nasledi tuđu istoriju.
   */
  async function retirePlan(plan: TestPlan) {
    const ok = await confirm({
      message: t(
        "quality.plan.confirmRetire",
        "Da povučem plan „{{plan}}“? Nalazi uzeti po njemu ostaju vezani za njega, a šifra ostaje zauzeta.",
        { plan: plan.code },
      ),
      danger: true,
    });
    if (!ok) return;
    try {
      await testPlanApi.retire(plan.id);
      setOpenId(null);
      setOpen(null);
      await load();
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  /** Zatvaranje starijeg plana kad dva važe za isti predmet: datum, ne brisanje. */
  async function closeOn(plan: TestPlan, validTo: string) {
    try {
      await testPlanApi.update(plan.id, { validTo });
      await refreshBoth();
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  async function removeItem(item: PlanItem) {
    if (!open) return;
    const naziv = item.description ?? item.parameterKey ?? "";
    const ok = await confirm({
      message: t(
        "quality.plan.confirmRemoveItem",
        "Da sklonim „{{naziv}}“ iz faze {{faza}}? Nalazi koji su već upisani ostaju.",
        { naziv, faza: phaseLabel(t, item.phase) },
      ),
    });
    if (!ok) return;
    try {
      await testPlanApi.removeItem(open.id, item.id);
      await refreshBoth();
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  if (error) return <div className="empty">{error}</div>;
  if (!plans || !catalogue) return <PanelLoading text={t("common.loading", "Učitavanje…")} />;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("quality.plan.title", "Testovi i plan kontrolisanja")}</h1>
          <div className="sub">
            {t(
              "quality.plan.lead",
              "Test se definiše jednom, a plan kaže u kojoj fazi se radi, sa kojim granicama i koliko često.",
            )}
          </div>
        </div>
        <div className="page-actions">
          <div className="seg">
            <button type="button" aria-pressed={tab === "plans"} onClick={() => setTab("plans")}>
              {t("quality.plan.tabPlans", "Planovi kontrolisanja")}
            </button>
            <button type="button" aria-pressed={tab === "phases"} onClick={() => setTab("phases")}>
              {t("quality.plan.tabByPhase", "Pregled po fazi")}
            </button>
            <button type="button" aria-pressed={tab === "catalogue"} onClick={() => setTab("catalogue")}>
              {t("quality.plan.tabCatalogue", "Testovi")}
            </button>
          </div>
          {smeDaVodi && tab === "plans" && (
            <button className="btn primary" type="button" onClick={() => setNewPlan({ replaces: null })}>
              <Ic.plus /> {t("quality.plan.newPlan", "Nov plan")}
            </button>
          )}
        </div>
      </div>

      {smeDaVodi && newPlan && (
        <PlanForm
          replaces={newPlan.replaces}
          lang={lang}
          cultures={Array.from(new Set((plans ?? []).map((p) => p.culture).filter(Boolean) as string[]))}
          onCancel={() => setNewPlan(null)}
          onDone={async (id) => {
            setNewPlan(null);
            setOpenId(id);
            await load();
          }}
        />
      )}

      {tab === "phases" ? (
        <PhaseBoard lang={lang} />
      ) : tab === "catalogue" ? (
        <div className="card">
          <header className="card-h">
            <div>
              <h3>{t("quality.plan.tabCatalogue", "Testovi")}</h3>
              <div className="sub">
                {t(
                  "quality.catalogue.lead",
                  "Uz svaki test stoji u kojim se fazama koristi. Test koji nije ni u jednoj fazi ne radi se nigde.",
                )}
              </div>
            </div>
          </header>
          <div className="card-b tight">
            <CatalogueTable rows={catalogue} lang={lang} />
          </div>
        </div>
      ) : (
        <div className="catalog-layout">
          <aside className="cat-side">
            <PlanGroup
              title={t("quality.plan.groupCultures", "Sirovine")}
              rows={grouped.cultures}
              openId={openId}
              onOpen={setOpenId}
              lang={lang}
            />
            <PlanGroup
              title={t("quality.plan.groupArticles", "Proizvodi")}
              rows={grouped.products}
              openId={openId}
              onOpen={setOpenId}
              lang={lang}
            />
            <PlanGroup
              title={t("quality.plan.groupGeneral", "Opšti planovi")}
              rows={grouped.general}
              openId={openId}
              onOpen={setOpenId}
              lang={lang}
            />
          </aside>

          <section style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
            {!open ? (
              <p style={tiho}>{t("quality.plan.pickOne", "Izaberite plan sa spiska.")}</p>
            ) : (
              <>
                <PlanHeader
                  plan={open}
                  lang={lang}
                  others={(plans ?? []).filter((p) => p.id !== open.id)}
                  smeDaVodi={smeDaVodi}
                  onReplace={() => setNewPlan({ replaces: open })}
                  onRetire={() => void retirePlan(open)}
                  onCloseOn={(datum) => void closeOn(open, datum)}
                />
                {QUALITY_PHASES.map((phase) => {
                  const items = open.items.filter((i) => i.phase === phase);
                  if (items.length === 0 && addingAt !== phase) {
                    return (
                      <EmptyPhase
                        key={phase}
                        phase={phase}
                        onAdd={() => setAddingAt(phase)}
                        smeDaVodi={smeDaVodi}
                      />
                    );
                  }
                  return (
                    <div key={phase} className="card">
                      <header className="card-h">
                        <div>
                          <h3>{phaseLabel(t, phase)}</h3>
                          <div className="sub">{phaseHint(t, phase)}</div>
                        </div>
                        {smeDaVodi && (
                          <button className="btn sm" type="button" onClick={() => setAddingAt(phase)}>
                            <Ic.plus /> {t("quality.plan.addTest", "Dodaj test")}
                          </button>
                        )}
                      </header>
                      {items.length > 0 && (
                        <div className="card-b tight">
                          <ItemTable
                            items={items}
                            lang={lang}
                            onEdit={setEditingItem}
                            onRemove={removeItem}
                            smeDaVodi={smeDaVodi}
                          />
                        </div>
                      )}
                      {smeDaVodi && addingAt === phase && (
                        <div className="card-b">
                          <ItemForm
                            planId={open.id}
                            phase={phase}
                            catalogue={catalogue}
                            steps={steps}
                            // U procesu isti test stoji na više koraka, pa se već upotrebljeni test ne
                            // sklanja sa spiska: vlaga posle ekstrakcije i vlaga posle mlevenja su dve
                            // propisane stavke. Pravi duplikat — isti test, isti korak — odbija servis.
                            usedParameterIds={
                              phase === "PROCESS"
                                ? []
                                : (items.map((i) => i.parameterId).filter(Boolean) as string[])
                            }
                            lang={lang}
                            onDone={async () => {
                              setAddingAt(null);
                              await refreshBoth();
                            }}
                            onCancel={() => setAddingAt(null)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </section>
        </div>
      )}

      {smeDaVodi && editingItem && open && (
        <ItemForm
          planId={open.id}
          phase={editingItem.phase}
          existing={editingItem}
          catalogue={catalogue}
          steps={steps}
          usedParameterIds={[]}
          lang={lang}
          onDone={async () => {
            setEditingItem(null);
            await refreshBoth();
          }}
          onCancel={() => setEditingItem(null)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ delovi */

function PlanGroup({
  title,
  rows,
  openId,
  onOpen,
  lang,
}: {
  title: string;
  rows: TestPlan[];
  openId: string | null;
  onOpen: (id: string) => void;
  lang: string;
}) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  return (
    <div className="group">
      <h4>{title}</h4>
      <div className="cat-list">
        {rows.map((p) => (
          <button
            key={p.id}
            type="button"
            className={p.id === openId ? "active" : ""}
            onClick={() => onOpen(p.id)}
            title={subjectLabel(t, p)}
          >
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {localizedName({ name: p.code, nameI18n: p.nameI18n }, lang)}
              {!p.inForce && ` · ${t("quality.plan.notInForce", "ne važi")}`}
            </span>
            <span className="n">{p.phases.reduce((n, f) => n + f.tests, 0)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Zaglavlje otvorenog plana, i dve radnje koje plan ima: zamena i povlačenje.
 *
 * Izmene plana nema namerno. Granica upisana preko stare učinila bi jučerašnji nalaz neobjašnjivim
 * pravilima koja su tada važila, pa se plan zatvara datumom i otvara se sledeći. Isti razlog po kojem se
 * ni pravila obračuna ne prepisuju.
 */
function PlanHeader({
  plan,
  lang,
  others,
  smeDaVodi,
  onReplace,
  onRetire,
  onCloseOn,
}: {
  plan: TestPlan;
  lang: string;
  others: TestPlan[];
  smeDaVodi: boolean;
  onReplace: () => void;
  onRetire: () => void;
  onCloseOn: (validTo: string) => void;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canDelete = hasPermission("section:test_plans:full");
  const bezGranice = plan.phases.reduce((n, f) => n + f.limitMissing, 0);
  // Dva plana za isti predmet mogu da važe u isto vreme — baza to ne brani, a zamena je dva poteza.
  // Nalaz se uzima po novijem, pa ekran to kaže i nudi da se stariji zatvori danom pre njega. Tvrdnja
  // važi samo dok OBA plana važe: zatvoren plan ne preklapa ništa, pa `plan.inForce` stoji u uslovu —
  // bez njega uredno zamenjen plan i dalje nosi upozorenje i nudi da se zatvori danom koji je prošao.
  const noviji = plan.inForce
    ? others
        .filter((p) => p.inForce && sameSubject(p, plan) && p.validFrom > plan.validFrom)
        .sort((a, b) => (a.validFrom < b.validFrom ? 1 : -1))[0]
    : undefined;
  return (
    <div className="card">
      <header className="card-h">
        <div>
          <h3>{localizedName({ name: plan.code, nameI18n: plan.nameI18n }, lang)}</h3>
          <div className="sub">
            {t("quality.plan.validFrom", "Važi od {{datum}}", { datum: formatDate(plan.validFrom) })}
            {plan.validTo
              ? ` ${t("quality.plan.validTo", "do {{datum}}", { datum: formatDate(plan.validTo) })}`
              : ""}
            {plan.note ? ` · ${plan.note}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span className="tag">{subjectLabel(t, plan)}</span>
          {smeDaVodi && (
            <>
              <button className="btn sm" type="button" onClick={onReplace}>
                {t("quality.plan.replace", "Zameni plan")}
              </button>
              <button className="btn sm" type="button" disabled={!canDelete} onClick={onRetire}>
                {t("quality.plan.retire", "Povuci plan")}
              </button>
            </>
          )}
        </div>
      </header>
      <div
        className="card-b"
        style={{ paddingTop: 12, paddingBottom: 12, display: "flex", flexDirection: "column", gap: 8 }}
      >
        <span style={tiho}>
          {t(
            "quality.plan.replacedNotEdited",
            "Plan se ne menja nego se zamenjuje: nalazi uzeti po ovom planu ostaju vezani za njega i posle. Zamena zatvara ovaj plan danom pre početka novog.",
          )}
        </span>
        {bezGranice > 0 && (
          <span className="tag" style={upozorenjeZnak}>
            {t(
              "quality.plan.limitsMissing",
              "Na {{n}} testova granica prihvatanja nije uneta, pa portal upozorava a ne odbija.",
              { n: bezGranice },
            )}
          </span>
        )}
        {noviji && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span className="tag" style={upozorenjeZnak}>
              {t(
                "quality.plan.overlap",
                "Za isti predmet važi i noviji plan „{{plan}}“ od {{datum}}; nalaz se uzima po njemu.",
                { plan: noviji.code, datum: formatDate(noviji.validFrom) },
              )}
            </span>
            {smeDaVodi && (
              <button className="btn sm" type="button" onClick={() => onCloseOn(dayBefore(noviji.validFrom))}>
                {t("quality.plan.closeBefore", "Zatvori ovaj plan {{datum}}", {
                  datum: formatDate(dayBefore(noviji.validFrom)),
                })}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Nov plan, ili zamena postojećeg.
 *
 * Zamena je namerno isti obrazac: predmet se preuzima od starog plana i ne menja se, jer plan za soju ne
 * postaje plan za kukuruz — to je drugi plan. Posle upisa novog, stari se zatvara danom pre njegovog
 * početka, i zato se traži datum početka koji je posle početka starog.
 */
function PlanForm({
  replaces,
  lang,
  cultures,
  onDone,
  onCancel,
}: {
  replaces: TestPlan | null;
  lang: string;
  cultures: string[];
  onDone: (id: string) => void | Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [culture, setCulture] = useState(replaces?.culture ?? "");
  const [general, setGeneral] = useState(replaces ? !replaces.culture : false);
  const [validFrom, setValidFrom] = useState(today());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    // Rano odbijanje: zamena koja počinje pre starog plana ne bi mogla da ga zatvori, a server bi javio
    // da bi se plan zatvorio pre nego što je počeo.
    if (replaces && validFrom <= replaces.validFrom) {
      toast.error(
        t("quality.plan.replaceAfter", "Nov plan mora da počne posle {{datum}}, kad počinje plan koji menja.", {
          datum: formatDate(replaces.validFrom),
        }),
      );
      return;
    }
    setBusy(true);
    try {
      const created = await testPlanApi.create({
        code: code.trim().toUpperCase(),
        nameI18n: name.trim() ? { sr: name.trim() } : undefined,
        culture: general ? null : culture.trim() || null,
        productId: replaces?.productId ?? null,
        validFrom,
        note: note.trim() || null,
      });
      if (replaces && (!replaces.validTo || replaces.validTo >= validFrom)) {
        // Dva poteza, i to se vidi: ako drugi padne, ostaju dva plana u važenju, a zaglavlje starog to
        // kaže i nudi da se zatvori. Obrnut red bi predmet ostavio bez plana.
        try {
          await testPlanApi.update(replaces.id, { validTo: dayBefore(validFrom) });
        } catch (e) {
          toast.error(
            t(
              "quality.plan.closeFailed",
              "Nov plan je upisan, ali stari nije zatvoren: {{greska}}. Zatvorite ga sa njegovog zaglavlja.",
              { greska: (e as ApiError).message },
            ),
          );
        }
      }
      await onDone(created.id);
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <header className="card-h">
        <div>
          <h3>
            {replaces
              ? t("quality.plan.replaceTitle", "Zamena plana „{{plan}}“", {
                  plan: localizedName({ name: replaces.code, nameI18n: replaces.nameI18n }, lang),
                })
              : t("quality.plan.newPlan", "Nov plan")}
          </h3>
          <div className="sub">
            {replaces
              ? t(
                  "quality.plan.replaceLead",
                  "Stari plan se zatvara danom pre početka novog. Njegove stavke ostaju kakve su, a nalazi uzeti po njemu ostaju vezani za njega.",
                )
              : t(
                  "quality.plan.newPlanLead",
                  "Plan dobija svoje stavke posle upisa, po fazama. Predmet je sirovina, ili plan važi za sve proizvode.",
                )}
          </div>
        </div>
      </header>
      <div className="card-b">
        <div style={mreza}>
          <Field
            label={t("quality.plan.field.code", "Šifra")}
            hint={t("quality.plan.codeHint", "Velika slova, cifre i crta; šifra se posle ne menja.")}
          >
            <input className="input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
          </Field>
          <Field label={t("quality.plan.field.name", "Naziv")}>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field
            label={t("quality.plan.field.validFrom", "Važi od")}
            hint={
              replaces
                ? t("quality.plan.validFromHint", "Dan pre ovog datuma stari plan prestaje da važi.")
                : undefined
            }
          >
            <input className="input" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          </Field>
          {replaces ? (
            <Field label={t("quality.plan.field.subject", "Predmet")}>
              <div style={{ paddingTop: 6 }}>
                <span className="tag">{subjectLabel(t, replaces)}</span>
              </div>
            </Field>
          ) : (
            <Field
              label={t("quality.plan.field.culture", "Sirovina")}
              hint={
                cultures.length > 0
                  ? t("quality.plan.cultureHint", "Već u upotrebi: {{spisak}}", { spisak: cultures.join(", ") })
                  : undefined
              }
            >
              <input
                className="input"
                value={culture}
                disabled={general}
                onChange={(e) => setCulture(e.target.value.toUpperCase())}
              />
            </Field>
          )}
          <Field label={t("quality.plan.field.note", "Napomena")} wide>
            <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          {!replaces && (
            <label>
              <input
                type="checkbox"
                checked={general}
                onChange={(e) => {
                  setGeneral(e.target.checked);
                  if (e.target.checked) setCulture("");
                }}
              />{" "}
              {t("quality.plan.field.general", "Važi za sve proizvode")}
            </label>
          )}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button className="btn" type="button" onClick={onCancel} disabled={busy}>
              {t("common.cancel", "Odustani")}
            </button>
            <button
              className="btn primary"
              type="button"
              onClick={() => void submit()}
              disabled={busy || !code.trim() || (!general && !replaces && !culture.trim())}
            >
              {t("common.save", "Sačuvaj")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Šta je propisano u jednoj fazi, kroz sve planove koji važe.
 *
 * Drugi ulaz u konfigurator, i jedini koji pokazuje istu fazu kroz više planova. Po planu se vidi šta se
 * radi sa sojom; ovde se vidi šta se radi na prijemu — pitanje koje ima onaj ko stoji na prijemu. Zato se
 * ovde uočava i razlika koja se po planovima ne vidi: pšenično stočno brašno jedino meri vlagu NIR-om, a
 * ostale četiri sirovine vlagomerom.
 */
function PhaseBoard({ lang }: { lang: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [tally, setTally] = useState<PhaseTally[] | null>(null);
  const [phase, setPhase] = useState<QualityPhase>("INTAKE");
  const [view, setView] = useState<PhaseView | null>(null);

  useEffect(() => {
    void testPlanApi
      .phaseTally()
      .then(setTally)
      .catch((e: ApiError) => {
        setTally([]);
        toast.error(e.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setView(null);
    void testPlanApi
      .byPhase(phase)
      .then(setView)
      .catch((e: ApiError) => toast.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const count = (f: QualityPhase) => (tally ?? []).find((r) => r.phase === f)?.tests ?? 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="card">
        <header className="card-h">
          <div>
            <h3>{t("quality.phaseBoard.title", "Propisano po fazama")}</h3>
            <div className="sub">
              {t(
                "quality.phaseBoard.lead",
                "Isti testovi, drugi ulaz: ne po planu nego po mestu u lancu, kroz sve planove koji važe.",
              )}
            </div>
          </div>
        </header>
        <div className="card-b">
          {/* Pet faza, pa ne `kpi-grid` sa četiri kolone: ovde se pet pločica slaže i na telefonu. */}
          <div style={plocice}>
            {QUALITY_PHASES.map((f) => (
              <button
                key={f}
                type="button"
                className="card"
                aria-pressed={phase === f}
                onClick={() => setPhase(f)}
                style={{
                  textAlign: "left",
                  cursor: "pointer",
                  padding: 12,
                  borderColor: phase === f ? "var(--brand-primary)" : undefined,
                }}
              >
                <div style={tiho}>{phaseLabel(t, f)}</div>
                <div style={{ fontSize: 22, fontVariantNumeric: "tabular-nums" }}>{count(f)}</div>
                <div style={{ ...tiho, fontSize: 11.5 }}>
                  {t("quality.phaseBoard.tests", "propisanih testova")}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {!view ? (
        <PanelLoading text={t("common.loading", "Učitavanje…")} />
      ) : view.groups.length === 0 ? (
        <div className="card">
          <div className="empty">
            {t("quality.phaseBoard.none", "U ovoj fazi nijedan plan ništa ne propisuje.")}
          </div>
        </div>
      ) : (
        view.groups.map((g) => (
          <div key={g.planId} className="card">
            <header className="card-h">
              <div>
                <h3>{localizedName({ name: g.planCode, nameI18n: g.planNameI18n }, lang)}</h3>
                <div className="sub">{subjectLabel(t, g)}</div>
              </div>
              <span className="tag neutral">
                {t("quality.plan.testsShort", "{{n}} testova", { n: g.items.length })}
              </span>
            </header>
            <div className="card-b tight">
              <ItemTable items={g.items} lang={lang} smeDaVodi={false} />
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function EmptyPhase({
  phase,
  onAdd,
  smeDaVodi,
}: {
  phase: QualityPhase;
  onAdd: () => void;
  smeDaVodi: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="card">
      <header className="card-h">
        <div>
          <h3>{phaseLabel(t, phase)}</h3>
          <div className="sub">{t("quality.plan.noTestsAtPhase", "Nijedan test nije propisan.")}</div>
        </div>
        {smeDaVodi && (
          <button className="btn sm" type="button" onClick={onAdd}>
            <Ic.plus /> {t("quality.plan.addTest", "Dodaj test")}
          </button>
        )}
      </header>
    </div>
  );
}

function ItemTable({
  items,
  lang,
  onEdit,
  onRemove,
  smeDaVodi,
}: {
  items: PlanItem[];
  lang: string;
  /** Nema ih u pogledu po fazama: tamo se stavke čitaju kroz sve planove, a menjaju se u svom planu. */
  onEdit?: (i: PlanItem) => void;
  onRemove?: (i: PlanItem) => void;
  smeDaVodi: boolean;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canDelete = hasPermission("section:test_plans:full");
  return (
    <table className="tbl">
      <thead>
        <tr>
          <th>{t("quality.plan.col.test", "Test")}</th>
          <th>{t("quality.plan.col.method", "Metoda")}</th>
          <th>{t("quality.plan.col.limits", "Granice")}</th>
          <th>{t("quality.plan.col.frequency", "Učestalost")}</th>
          <th>{t("quality.plan.col.who", "Unosi / kontroliše")}</th>
          {smeDaVodi && <th aria-label={t("common.actions", "Akcije")} />}
          <th />
        </tr>
      </thead>
      <tbody>
        {items.map((i) => (
          <tr key={i.id}>
            <td>
              <div className="uz-oznake">
              {i.description ? (
                <>
                  <span>{i.description}</span>
                  <span className="tag" title={t("quality.plan.descriptiveHint", "Potvrđuje se pregledom, ne meri se")}>
                    {t("quality.plan.descriptive", "pregled")}
                  </span>
                </>
              ) : (
                <>
                  <strong>{localizedName({ name: i.parameterKey ?? "", nameI18n: i.parameterNameI18n }, lang)}</strong>
                  {i.unit ? <span style={tiho}>{i.unit}</span> : null}
                  {i.resultType && i.resultType !== "NUMERIC" && (
                    <span className="tag">{resultTypeLabel(t, i.resultType)}</span>
                  )}
                </>
              )}
              {!i.mandatory && <span className="tag">{t("quality.plan.optional", "nije obavezan")}</span>}
              {i.external && <span className="tag">{t("quality.plan.external", "spoljna laboratorija")}</span>}
              {/* Korak se vidi uz test, jer vlaga posle ekstrakcije i vlaga posle mlevenja nisu isti red. */}
              {i.productionStepCode && <span className="tag neutral">{i.productionStepCode}</span>}
              {i.phase === "PROCESS" && !i.productionStepCode && (
                <span className="tag" style={upozorenjeZnak}>
                  {t("quality.plan.noStep", "korak nije određen")}
                </span>
              )}
              </div>
            </td>
            <td style={tiho}>{i.method ?? "—"}</td>
            <td>
              {i.limitMissing ? (
                <span className="tag" style={upozorenjeZnak}>{t("quality.plan.noLimit", "nije uneta")}</span>
              ) : (
                <>
                  {granicaTekst(i, t)}
                  <div style={{ ...tiho, fontSize: 11.5 }}>{outOfRangeLabel(t, i.outOfRangeAction)}</div>
                </>
              )}
            </td>
            <td style={tiho}>{frequencyLabel(t, i.frequencyBasis, i.frequencyCount)}</td>
            <td style={tiho}>
              {i.enteredByRole ?? "—"}
              {i.checkedByRole ? ` / ${i.checkedByRole}` : ""}
            </td>
            {smeDaVodi && onEdit && onRemove && (
              <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <button className="btn sm" type="button" onClick={() => onEdit(i)}>
                  {t("common.edit", "Izmeni")}
                </button>{" "}
                <button className="btn sm" type="button" disabled={!canDelete} onClick={() => onRemove(i)}>
                  {t("common.remove", "Skloni")}
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CatalogueTable({ rows, lang }: { rows: QualityCatalogueEntry[]; lang: string }) {
  const { t } = useTranslation();
  return (
    <table className="tbl">
        <thead>
          <tr>
            <th>{t("quality.catalogue.col.test", "Test")}</th>
            <th>{t("quality.catalogue.col.unit", "Jedinica")}</th>
            <th>{t("quality.catalogue.col.result", "Rezultat")}</th>
            <th>{t("quality.catalogue.col.phases", "Koristi se u fazama")}</th>
            <th>{t("quality.catalogue.col.count", "Propisan")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={r.active ? undefined : { opacity: 0.55 }}>
              <td>
                <strong>{localizedName({ name: r.key, nameI18n: r.nameI18n }, lang)}</strong>
                <div style={{ ...tiho, fontSize: 11.5 }}>{r.key}</div>
              </td>
              <td style={tiho}>{r.unit || "—"}</td>
              <td style={tiho}>{resultTypeLabel(t, r.resultType)}</td>
              <td>
                {r.usedInPhases.length === 0 ? (
                  <span className="tag" style={upozorenjeZnak}>{t("quality.catalogue.unused", "nigde")}</span>
                ) : (
                  r.usedInPhases.map((f) => (
                    <span key={f} className="tag">
                      {phaseLabel(t, f)}
                    </span>
                  ))
                )}
              </td>
              <td style={tiho}>
                {t("quality.catalogue.timesPrescribed", "{{n}}x", { n: r.timesPrescribed })}
              </td>
            </tr>
          ))}
      </tbody>
    </table>
  );
}

/* ------------------------------------------------------------------ obrazac stavke */

function ItemForm({
  planId,
  phase,
  existing,
  catalogue,
  steps,
  usedParameterIds,
  lang,
  onDone,
  onCancel,
}: {
  planId: string;
  phase: QualityPhase;
  existing?: PlanItem;
  catalogue: QualityCatalogueEntry[];
  steps: ProductionStep[];
  usedParameterIds: string[];
  lang: string;
  onDone: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const izmena = Boolean(existing);
  const opisna = existing ? Boolean(existing.description) : false;

  const [kind, setKind] = useState<"test" | "inspection">(opisna ? "inspection" : "test");
  const [parameterId, setParameterId] = useState(existing?.parameterId ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [method, setMethod] = useState(existing?.method ?? "");
  const [mandatory, setMandatory] = useState(existing?.mandatory ?? true);
  const [acceptMin, setAcceptMin] = useState(existing?.acceptMin?.toString() ?? "");
  const [acceptMax, setAcceptMax] = useState(existing?.acceptMax?.toString() ?? "");
  const [technicalMin, setTechnicalMin] = useState(existing?.technicalMin?.toString() ?? "");
  const [technicalMax, setTechnicalMax] = useState(existing?.technicalMax?.toString() ?? "");
  const [usualMin, setUsualMin] = useState(existing?.usualMin?.toString() ?? "");
  const [usualMax, setUsualMax] = useState(existing?.usualMax?.toString() ?? "");
  // Prazno znači da granica nije uneta, a ne da je negativna: tri stanja, ne dva.
  const [acceptBool, setAcceptBool] = useState<"" | "true" | "false">(
    existing?.acceptBool == null ? "" : existing.acceptBool ? "true" : "false",
  );
  const [action, setAction] = useState<OutOfRangeAction | "">(existing?.outOfRangeAction ?? "");
  const [basis, setBasis] = useState<QualityFrequencyBasis>(existing?.frequencyBasis ?? defaultBasis(phase));
  const [count, setCount] = useState(existing?.frequencyCount?.toString() ?? "1");
  const [enteredByRole, setEnteredByRole] = useState(existing?.enteredByRole ?? defaultEnteredBy(phase));
  const [checkedByRole, setCheckedByRole] = useState(existing?.checkedByRole ?? "");
  const [external, setExternal] = useState(existing?.external ?? false);
  const [note, setNote] = useState(existing?.note ?? "");
  const [productionStepId, setProductionStepId] = useState("");
  const [busy, setBusy] = useState(false);

  const izabrani = catalogue.find((c) => c.id === parameterId) ?? null;
  // Da-ne i opisni test ne nose brojčanu granicu: GMO nije „iznad 0,5" nego pozitivan ili negativan.
  const nosiGranicu = kind === "test" && (izabrani?.resultType ?? "NUMERIC") === "NUMERIC";
  // Da-ne test granicu IMA — samo ona nije broj nego strana. Bez ovoga je „GMO negativan" ostajao
  // neunet, pa je jedini test koji se radi na svakom kamionu bio i jedini bez granice.
  const nosiStranu = kind === "test" && izabrani?.resultType === "BOOLEAN";
  const slobodni = catalogue.filter(
    (c) => c.active && (c.id === parameterId || !usedParameterIds.includes(c.id)),
  );

  async function submit() {
    setBusy(true);
    try {
      const body: PlanItemBody = {
        phase,
        method: method.trim() || null,
        mandatory,
        technicalMin: nosiGranicu ? num(technicalMin) : null,
        technicalMax: nosiGranicu ? num(technicalMax) : null,
        usualMin: nosiGranicu ? num(usualMin) : null,
        usualMax: nosiGranicu ? num(usualMax) : null,
        acceptMin: nosiGranicu ? num(acceptMin) : null,
        acceptMax: nosiGranicu ? num(acceptMax) : null,
        acceptBool: nosiStranu && acceptBool !== "" ? acceptBool === "true" : null,
        outOfRangeAction: action || null,
        frequencyBasis: basis,
        frequencyCount: num(count) ?? 1,
        enteredByRole: enteredByRole.trim() || null,
        checkedByRole: checkedByRole.trim() || null,
        external,
        note: note.trim() || null,
      };
      // Korak ide samo pri dodavanju i samo u procesu: drugde predmet merenja nije operacija nego
      // kamion, LOT ili nalog, pa bi zalepljen korak lagao. Server to isto odbija. Pri izmeni ga nema
      // jer premeštanje testa na drugi korak nije ispravka nego druga propisana stavka.
      const korak = phase === "PROCESS" && productionStepId ? productionStepId : null;
      if (izmena && existing) {
        if (opisna) body.description = description.trim() || null;
        await testPlanApi.updateItem(planId, existing.id, body);
      } else if (kind === "test") {
        await testPlanApi.addItem(planId, { ...body, productionStepId: korak, parameterId });
      } else {
        await testPlanApi.addItem(planId, {
          ...body,
          productionStepId: korak,
          description: description.trim(),
        });
      }
      await onDone();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="sub" style={{ marginBottom: 10 }}>
        {izmena
          ? t("quality.plan.editItem", "Izmena propisanog testa, faza {{faza}}", { faza: phaseLabel(t, phase) })
          : t("quality.plan.newItem", "Nov propisan test, faza {{faza}}", { faza: phaseLabel(t, phase) })}
      </div>

      {!izmena && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ marginRight: 16 }}>
            <input type="radio" checked={kind === "test"} onChange={() => setKind("test")} />{" "}
            {t("quality.plan.kindTest", "Test iz kataloga")}
          </label>
          <label>
            <input type="radio" checked={kind === "inspection"} onChange={() => setKind("inspection")} />{" "}
            {t("quality.plan.kindInspection", "Pregled, opisno")}
          </label>
        </div>
      )}

      <div style={mreza}>
        {kind === "test" && !izmena && (
          <Field label={t("quality.plan.field.test", "Test")}>
            <Select
              value={parameterId}
              onChange={(v) => setParameterId(v)}
              placeholder={t("quality.plan.pickTest", "— izaberite —")}
              options={slobodni.map((c) => ({
                value: c.id,
                label:
                  localizedName({ name: c.key, nameI18n: c.nameI18n }, lang) + (c.unit ? ` (${c.unit})` : ""),
              }))}
            />
          </Field>
        )}

        {(kind === "inspection" || opisna) && (
          <Field label={t("quality.plan.field.description", "Šta se pregleda")} wide>
            <input
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("quality.plan.descriptionPlaceholder", "npr. zdravo zrno, bez stranog mirisa")}
            />
          </Field>
        )}

        {phase === "PROCESS" && !izmena && (
          <Field
            label={t("quality.plan.field.step", "Korak proizvodnje")}
            hint={
              steps.length === 0
                ? t("quality.plan.noStepsYet", "Šifarnik koraka je prazan; test ostaje na celoj fazi procesa.")
                : t("quality.plan.stepHint", "Vlaga posle ekstrakcije i vlaga posle mlevenja su dva propisana testa.")
            }
          >
            <Select
              value={productionStepId}
              onChange={(v) => setProductionStepId(v)}
              disabled={steps.length === 0}
              placeholder={t("quality.plan.wholeProcess", "— cela faza procesa —")}
              options={steps.map((s) => ({
                value: s.id,
                label: localizedName({ name: s.code, nameI18n: s.nameI18n }, lang),
              }))}
            />
          </Field>
        )}

        {phase === "PROCESS" && izmena && (
          <Field
            label={t("quality.plan.field.step", "Korak proizvodnje")}
            hint={t(
              "quality.plan.stepNotMoved",
              "Korak se ovde ne menja: test na drugom koraku je druga propisana stavka, pa se stara skloni i upiše nova.",
            )}
          >
            <div style={{ paddingTop: 6 }}>
              <span className="tag neutral">
                {existing?.productionStepCode ?? t("quality.plan.wholeProcess", "— cela faza procesa —")}
              </span>
            </div>
          </Field>
        )}

        <Field label={t("quality.plan.field.method", "Metoda ili uređaj")}>
          <input className="input" value={method} onChange={(e) => setMethod(e.target.value)} />
        </Field>

        {nosiGranicu && (
          <>
            <Field label={t("quality.plan.field.acceptMin", "Prijemna granica, najmanje")}>
              <input className="input" value={acceptMin} onChange={(e) => setAcceptMin(e.target.value)} />
            </Field>
            <Field label={t("quality.plan.field.acceptMax", "Prijemna granica, najviše")}>
              <input className="input" value={acceptMax} onChange={(e) => setAcceptMax(e.target.value)} />
            </Field>
            <Field label={t("quality.plan.field.outOfRange", "Van granice")}>
              <Select
                value={action}
                onChange={(v) => setAction(v as OutOfRangeAction | "")}
                options={[
                  { value: "" as OutOfRangeAction | "", label: outOfRangeLabel(t, null) },
                  ...OUT_OF_RANGE_ACTIONS.map((a) => ({ value: a, label: outOfRangeLabel(t, a) })),
                ]}
              />
            </Field>
            <Field
              label={t("quality.plan.field.technical", "Tehnički raspon")}
              hint={t("quality.plan.technicalHint", "Vrednost van njega portal odbija kao grešku u unosu.")}
              wide
            >
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  className="input"
                  value={technicalMin}
                  onChange={(e) => setTechnicalMin(e.target.value)}
                  placeholder={t("quality.plan.from", "od")}
                />
                <input
                  className="input"
                  value={technicalMax}
                  onChange={(e) => setTechnicalMax(e.target.value)}
                  placeholder={t("quality.plan.to", "do")}
                />
              </div>
            </Field>
            <Field
              label={t("quality.plan.field.usual", "Uobičajeni raspon")}
              hint={t(
                "quality.plan.usualHint",
                "Vrednost van njega portal prima i označava; granicu prihvatanja ne menja.",
              )}
              wide
            >
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  className="input"
                  value={usualMin}
                  onChange={(e) => setUsualMin(e.target.value)}
                  placeholder={t("quality.plan.from", "od")}
                />
                <input
                  className="input"
                  value={usualMax}
                  onChange={(e) => setUsualMax(e.target.value)}
                  placeholder={t("quality.plan.to", "do")}
                />
              </div>
            </Field>
          </>
        )}

        {nosiStranu && (
          <Field
            label={t("quality.plan.field.acceptBool", "Prihvaćen rezultat")}
            hint={t(
              "quality.plan.acceptBoolHint",
              "Da-ne test nema broj nego stranu: GMO se prima kao negativan.",
            )}
          >
            <Select
              value={acceptBool}
              onChange={(v) => setAcceptBool(v as "" | "true" | "false")}
              options={[
                { value: "" as "" | "true" | "false", label: t("quality.plan.acceptBool.none", "nije uneta") },
                { value: "false", label: t("quality.plan.acceptBool.negative", "negativan (odsustvo)") },
                { value: "true", label: t("quality.plan.acceptBool.positive", "pozitivan (prisustvo)") },
              ]}
            />
          </Field>
        )}

        <Field label={t("quality.plan.field.frequency", "Učestalost")}>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              className="input"
              style={{ maxWidth: 90 }}
              value={count}
              onChange={(e) => setCount(e.target.value)}
            />
            <Select
              value={basis}
              onChange={(v) => setBasis(v as QualityFrequencyBasis)}
              options={FREQUENCY_BASES.map((b) => ({ value: b, label: frequencyLabel(t, b, num(count) ?? 1) }))}
            />
          </div>
        </Field>

        <Field
          label={t("quality.plan.field.enteredBy", "Unosi")}
          hint={
            phase === "PROCESS"
              ? t("quality.plan.processWhoHint", "U procesu unose vođe smena, kontroliše tehnolog.")
              : undefined
          }
        >
          <input className="input" value={enteredByRole} onChange={(e) => setEnteredByRole(e.target.value)} />
        </Field>
        <Field label={t("quality.plan.field.checkedBy", "Kontroliše")}>
          <input className="input" value={checkedByRole} onChange={(e) => setCheckedByRole(e.target.value)} />
        </Field>

        <Field label={t("quality.plan.field.note", "Napomena")} wide>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
        <label>
          <input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} />{" "}
          {t("quality.plan.field.mandatory", "Obavezan")}
        </label>
        <label>
          <input type="checkbox" checked={external} onChange={(e) => setExternal(e.target.checked)} />{" "}
          {t("quality.plan.field.external", "Radi spoljna laboratorija")}
        </label>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button className="btn" type="button" onClick={onCancel} disabled={busy}>
            {t("common.cancel", "Odustani")}
          </button>
          <button
            className="btn primary"
            type="button"
            onClick={() => void submit()}
            disabled={busy || (kind === "test" && !izmena && !parameterId) || (kind === "inspection" && !description.trim())}
          >
            {t("common.save", "Sačuvaj")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  wide,
  children,
}: {
  label: string;
  hint?: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={wide ? { gridColumn: "1 / -1" } : undefined}>
      <div style={{ ...tiho, marginBottom: 4 }}>{label}</div>
      {children}
      {hint && <div style={{ ...tiho, fontSize: 11.5, marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ pomoćno */

function granicaTekst(i: PlanItem, t: TFunction): string {
  // Da-ne test ima granicu bez broja. Bez ovog reda je stavka sa unetom stranom prikazivala crtu,
  // što se čita kao „granica nije uneta" — a jeste.
  if (i.acceptBool != null) {
    return i.acceptBool
      ? t("quality.plan.acceptBool.positive", "pozitivan (prisustvo)")
      : t("quality.plan.acceptBool.negative", "negativan (odsustvo)");
  }
  const od = i.acceptMin;
  const doV = i.acceptMax;
  if (od != null && doV != null) return `${od} – ${doV}`;
  if (od != null) return `min ${od}`;
  if (doV != null) return `max ${doV}`;
  return "—";
}

/**
 * Predmet plana jednom rečju, za svaki prikaz plana isti. Prazno i za sirovinu i za proizvod znači da
 * plan važi za sve artikle. Ključ je imenovan po podatku (predmet je proizvod iz kataloga, veza je
 * `product_id`), a tekst nosi reč kojom portal svuda imenuje stavku kataloga — „artikal".
 */
function subjectLabel(t: TFunction, p: { culture: string | null; productId: string | null }): string {
  if (p.culture) return p.culture;
  if (p.productId) return t("quality.plan.oneProduct", "jedan artikal");
  return t("quality.plan.allProducts", "svi artikli");
}

/** Isti predmet: ista sirovina, isti proizvod, ili oba opšta. Po tome se zna šta koga zamenjuje. */
function sameSubject(a: TestPlan, b: TestPlan): boolean {
  return (a.culture ?? "") === (b.culture ?? "") && (a.productId ?? "") === (b.productId ?? "");
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Dan pre datog dana, u ISO zapisu. Tim danom stari plan prestaje da važi kad novi počne. */
function dayBefore(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

function num(s: string): number | null {
  const v = s.trim().replace(",", ".");
  if (v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Podrazumevana osnova po fazi: kamion na prijemu, lot dalje — ono što odgovori i kažu. */
function defaultBasis(phase: QualityPhase): QualityFrequencyBasis {
  if (phase === "INTAKE" || phase === "DISPATCH") return "PER_TRUCK";
  if (phase === "PROCESS") return "PER_BATCH";
  return "PER_LOT";
}

function defaultEnteredBy(phase: QualityPhase): string {
  return phase === "PROCESS" ? "Vođa smene" : "KVALITET";
}

/* ------------------------------------------------------------------ stil */

/*
 * Ekran stoji na sistemu: `catalog-layout` i `cat-side` za dva dela, `card`/`card-h`/`card-b` za
 * panele, `tbl` za tabele, `tag` za značke, `seg` za prekidač, `empty` za prazno. Ovde ostaje samo
 * ono čega u sistemu nema: boja upozorenja na znački, i mreža polja u obrascu.
 */

/** Sitan sivi tekst; isti par koji `ContractsPage` i `OrderEditorPage` već koriste. */
const tiho: CSSProperties = { color: "var(--ink-3)", fontSize: 12.5 };

/** Znacka koja nosi upozorenje — ista `tag`, samo u boji upozorenja. */
const upozorenjeZnak: CSSProperties = {
  borderColor: "var(--warn)",
  color: "var(--warn)",
  background: "transparent",
};


const mreza: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
  gap: 12,
};

/** Pločice faza: pet ih je, pa se broj kolona računa iz širine a ne zadaje. */
const plocice: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: 12,
};
